/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { generateDeclineScore, generateFamilyUpdate, getCareAiReports } from '@/lib/careAi';

const errMsg = (err, fallback) => err?.response?.data?.detail || fallback;

/** State + actions for the Care AI panel (reports history, decline score, family update). */
export const useCareAi = (teamId, { canGenerate }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'decline' | 'family' | null

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const res = await getCareAiReports(teamId);
      setReports(res.items || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(errMsg(err, 'Errore caricamento report AI'));
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okMsg) => {
    if (!canGenerate || busy) return null;
    setBusy(key);
    try {
      const res = await fn();
      setReports((prev) => [res.report, ...prev]);
      toast.success(okMsg);
      return res.report;
    } catch (err) {
      toast.error(errMsg(err, 'Errore generazione AI'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const runDecline = (opts) => run('decline', () => generateDeclineScore(teamId, opts), 'Punteggio di declino calcolato');
  const runFamily = (opts) => run('family', () => generateFamilyUpdate(teamId, opts), 'Aggiornamento per i familiari generato');

  const latest = (kind) => reports.find((r) => r.kind === kind) || null;

  return { reports, loading, busy, runDecline, runFamily, latest, reload: load };
};
